/**
 * useNotificationStream Hook
 *
 * Connects to the SSE notification stream endpoint for real-time
 * notification delivery. Automatically reconnects on connection loss
 * with exponential backoff. Updates the notification store and
 * triggers toasts for high-priority notification types.
 *
 * Handles token expiration gracefully by subscribing to auth store
 * changes and reconnecting with fresh tokens after refresh.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNotificationStore } from '../stores/notificationStore';
import { useAuthStore } from '../stores/authStore';
import { useCurrentWorkspaceId } from './useStoreSelectors';
import { API_BASE_URL } from '../config/env';
import type { Notification } from '@mellify/shared';

/** High-priority notification types that trigger a toast */
const HIGH_PRIORITY_TYPES = new Set([
  'escalation',
  'crisis_alert',
  'publish_failed',
]);

/** Maximum reconnection delay in milliseconds (30 seconds) */
const MAX_RECONNECT_DELAY = 30_000;

/** Base reconnection delay in milliseconds (1 second) */
const BASE_RECONNECT_DELAY = 1_000;

/**
 * Delay before reconnecting after an error, to allow token refresh
 * to complete if the error was caused by an expired token.
 */
const TOKEN_REFRESH_GRACE_DELAY = 2_000;

export interface UseNotificationStreamOptions {
  /** Whether to show toasts for high-priority notifications. Default: true */
  showToasts?: boolean;
  /** Custom callback when a new notification arrives */
  onNotification?: (notification: Notification) => void;
}

export function useNotificationStream(options: UseNotificationStreamOptions = {}) {
  const { showToasts = true, onNotification } = options;

  const workspaceId = useCurrentWorkspaceId();
  const tokens = useAuthStore((state) => state.tokens);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const addNotification = useNotificationStore((state) => state.addNotification);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  /** Track the token used for the current/last connection attempt */
  const connectedTokenRef = useRef<string | null>(null);
  /** Whether we are waiting for a token refresh before reconnecting */
  const waitingForTokenRefreshRef = useRef(false);

  /**
   * Dispatch a custom event that the NotificationToast component listens for.
   */
  const dispatchToastEvent = useCallback((notification: Notification) => {
    if (!showToasts) return;
    if (!HIGH_PRIORITY_TYPES.has(notification.type)) return;

    const event = new CustomEvent('mellify:notification-toast', {
      detail: notification,
    });
    window.dispatchEvent(event);
  }, [showToasts]);

  /**
   * Dispatch a custom event to trigger bell animation.
   */
  const dispatchBellAnimationEvent = useCallback(() => {
    const event = new CustomEvent('mellify:notification-bell-ping');
    window.dispatchEvent(event);
  }, []);

  /**
   * Disconnect from the SSE stream and clear any pending reconnect timers.
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
    waitingForTokenRefreshRef.current = false;
  }, []);

  /**
   * Connect to the SSE stream using the current access token.
   */
  const connect = useCallback(() => {
    // Read the latest token directly from the store to avoid stale closures
    const currentState = useAuthStore.getState();
    const currentToken = currentState.tokens?.accessToken;
    const currentIsAuthenticated = currentState.isAuthenticated;

    if (!workspaceId || !currentToken || !currentIsAuthenticated) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Track which token we're connecting with
    connectedTokenRef.current = currentToken;
    waitingForTokenRefreshRef.current = false;

    const url = `${API_BASE_URL}/workspaces/${workspaceId}/notifications/stream?token=${encodeURIComponent(currentToken)}`;

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', () => {
      // Reset reconnection attempts on successful connection
      reconnectAttemptsRef.current = 0;
    });

    eventSource.addEventListener('notification', (event: MessageEvent) => {
      if (!mountedRef.current) return;

      try {
        const notification = JSON.parse(event.data) as Notification;

        // Add to Zustand store
        addNotification(notification);

        // Trigger bell animation
        dispatchBellAnimationEvent();

        // Show toast for high-priority notifications
        dispatchToastEvent(notification);

        // Call custom callback
        onNotification?.(notification);
      } catch (error) {
        console.error('[useNotificationStream] Failed to parse notification:', error);
      }
    });

    eventSource.onerror = () => {
      if (!mountedRef.current) return;

      // Close the errored connection
      eventSource.close();
      eventSourceRef.current = null;

      // Check if the token has already been refreshed (by the Axios interceptor)
      // since we established this connection
      const latestState = useAuthStore.getState();
      const latestToken = latestState.tokens?.accessToken;
      const hasNewToken = latestToken && latestToken !== connectedTokenRef.current;

      if (hasNewToken) {
        // Token was already refreshed — reconnect immediately with the new token
        reconnectAttemptsRef.current = 0;
        if (mountedRef.current) {
          connect();
        }
        return;
      }

      // Token hasn't changed yet — the Axios interceptor may be in the process
      // of refreshing. Set a flag so the auth store subscription can trigger
      // reconnection when the new token arrives.
      waitingForTokenRefreshRef.current = true;

      // Still schedule a fallback reconnection with exponential backoff,
      // but add a grace period on the first attempt to allow token refresh.
      const attempts = reconnectAttemptsRef.current;
      const baseDelay = attempts === 0
        ? TOKEN_REFRESH_GRACE_DELAY
        : BASE_RECONNECT_DELAY * Math.pow(2, attempts);
      const delay = Math.min(baseDelay, MAX_RECONNECT_DELAY);

      reconnectAttemptsRef.current = attempts + 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          // Check authentication state before reconnecting
          const state = useAuthStore.getState();
          if (state.isAuthenticated && state.tokens?.accessToken) {
            connect();
          }
        }
      }, delay);
    };
  }, [
    workspaceId,
    addNotification,
    dispatchBellAnimationEvent,
    dispatchToastEvent,
    onNotification,
  ]);

  // Subscribe to auth store token changes to reconnect with fresh tokens
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe(
      (state, prevState) => {
        if (!mountedRef.current) return;

        const newToken = state.tokens?.accessToken;
        const oldToken = prevState.tokens?.accessToken;

        // If the user logged out, disconnect
        if (!state.isAuthenticated && prevState.isAuthenticated) {
          disconnect();
          return;
        }

        // If the token changed (refresh occurred) and we're waiting for it,
        // reconnect immediately with the new token
        if (
          newToken &&
          newToken !== oldToken &&
          state.isAuthenticated &&
          waitingForTokenRefreshRef.current
        ) {
          // Cancel the pending exponential backoff reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }

          reconnectAttemptsRef.current = 0;
          waitingForTokenRefreshRef.current = false;
          connect();
        }
      }
    );

    return unsubscribe;
  }, [connect, disconnect]);

  // Connect when workspace changes, disconnect on cleanup
  useEffect(() => {
    mountedRef.current = true;

    if (isAuthenticated && tokens?.accessToken && workspaceId) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [workspaceId, isAuthenticated, connect, disconnect, tokens?.accessToken]);

  return {
    /** Manually reconnect to the SSE stream */
    reconnect: connect,
    /** Manually disconnect from the SSE stream */
    disconnect,
  };
}